/**
 * 返回手势守卫（浏览器后退 / 微信左缘右滑 / 安卓返回键）
 * -----------------------------------------------------------------------------
 * 手机上没有「关闭浮层」这个键：iOS 从屏幕左缘右滑、安卓三键导航的返回、
 * 微信安卓的返回，走的都是 **history back**。所以「看图时返回 = 关图」
 * 「填报名时返回 = 先确认」这两件事只能落到同一条链路上 —— 拦 `popstate`。
 *
 * 做法是业界通用的「哨兵记录」：
 *   ① 需要拦返回时，压一条**同 URL** 的历史记录（哨兵）；
 *   ② 手势返回只弹掉哨兵，URL 不变 → 页面不动、路由不跳；
 *   ③ `popstate` 里问最上层的守卫「留下 / 走 / 本层结束」；
 *   ④ `stay` → 重新压哨兵；`leave` → 再退一格回到真正的上一页；
 *      `dismiss` → 什么都不做（历史已经自然退回一位，正好是打开本层之前的状态）。
 *
 * 为什么是**栈**而不是单个回调：报名表单里会再打开图片查看，两层守卫叠在一起。
 * 后注册的（图片）在栈顶，第一次返回先关图片，再返回才轮到表单确认。
 * 若两层各挂各的 `popstate`，同一次手势会把两层都触发一遍 ——
 * 图片关了，退出确认框也一起弹出来。
 *
 * ⚠️ 自己发起的后退必须抑制一次：`leave` 与「卸载时清哨兵」都会调
 * `history.back()`，那会再触发一次 `popstate`。若不吞掉，这一次后退会落到
 * **下一层**守卫头上（表单层收到本属于图片层的返回，凭空弹一个确认框）。
 */

/** 守卫对一次返回的裁决 */
export type BackDecision =
  /** 留在原页（会自动补一条哨兵，下次返回还能拦住） */
  | 'stay'
  /** 真的离开（再退一格回到上一页） */
  | 'leave'
  /** 本层结束但页面不跳（关掉图片查看这类） */
  | 'dismiss';

interface Layer {
  onBack: () => BackDecision | Promise<BackDecision>;
  /** 已离开 / 已销毁 / 已放弃：后续 `popstate` 不再打扰它 */
  done: boolean;
  /** 我们压的那条哨兵是否还在历史里 */
  sentinelAlive: boolean;
}

const layers: Layer[] = [];
let bound = false;
/** 我们主动发起的后退，吞掉随之而来的那一次 popstate */
let swallowNextPop = false;

function pushSentinel(layer: Layer): void {
  layer.sentinelAlive = true;
  window.history.pushState({ __backGuard: true }, '');
}

/** 我们主动后退一格（吞掉自己引发的 popstate） */
function backProgrammatically(): void {
  swallowNextPop = true;
  window.history.back();
}

function dropLayer(layer: Layer): void {
  layer.done = true;
  const index = layers.indexOf(layer);
  if (index >= 0) layers.splice(index, 1);
}

function settle(layer: Layer, decision: BackDecision): void {
  if (layer.done) return;

  if (decision === 'stay') {
    // 裁决期间哨兵已被弹掉，补一条回来
    pushSentinel(layer);
    return;
  }

  dropLayer(layer);
  // `leave`：历史退了一位只弹掉哨兵，还要再退一位才是真正的上一页。
  // `dismiss`：不再动历史 —— 现状正好是「打开本层之前」。
  if (decision === 'leave') backProgrammatically();
}

function onPopState(): void {
  if (swallowNextPop) {
    swallowNextPop = false;
    return;
  }

  const layer = layers[layers.length - 1];
  if (!layer || layer.done) return;

  // 哨兵刚被弹掉，先记下来：`stay` 会重压，其它两种保持「没有哨兵」的状态
  layer.sentinelAlive = false;

  let decision: BackDecision | Promise<BackDecision>;
  try {
    decision = layer.onBack();
  } catch {
    // 守卫自己抛错不能把用户卡成「返回无效」：按离开处理
    dropLayer(layer);
    return;
  }

  if (typeof decision === 'string') settle(layer, decision);
  else void decision.then((value) => settle(layer, value)).catch(() => undefined);
}

export interface BackGuard {
  /**
   * 撤销这一层。
   *
   * @param dropSentinel 是否顺手把自己压的哨兵弹掉。**同一页内的浮层必须传 true**
   *   （图片查看：哨兵留着的话，用户关图后第一次点返回只会弹掉哨兵 —— 看起来
   *   「返回没反应」，而且这次 `popstate` 会落到下一层守卫头上，凭空弹个退出确认）。
   *   页面级守卫（报名表单）**不能传 true**：它卸载的原因往往是*已经*跳到新页面了
   *   （提交成功 → 报名成功页），此时 `history.back()` 会把刚 push 的新页面弹掉。
   */
  release: (dropSentinel?: boolean) => void;
}

/**
 * 「本层确认要离开了」——摘掉栈顶守卫并退回上一页。
 *
 * 专给「先补哨兵、再弹确认框」这种写法用（报名表单的退出确认）：
 * 守卫同步返回 `stay` 会重新压一条哨兵，所以离开时必须退**两格**（哨兵 + 本页）。
 * 不能改用 `release(true)` —— 那只退一格，弹掉的正好是刚补的哨兵，
 * 人还停在原页，表现成「点了退出却没走」（2026-10-26 实测踩到）。
 *
 * `go(-2)` 只派发**一次** `popstate`（一次跳跃一个事件），吞掉即可。
 *
 * @returns 是否有人接管。`false` 表示当前没有守卫，调用方自己收场。
 */
export function leaveTopGuard(): boolean {
  const layer = layers[layers.length - 1];
  if (!layer || layer.done) return false;

  const hadSentinel = layer.sentinelAlive;
  dropLayer(layer);
  swallowNextPop = true;
  window.history.go(hadSentinel ? -2 : -1);
  return true;
}

/**
 * 压一层返回守卫。
 *
 * @param onBack 返回被拦截时的裁决；可以是 `Promise`，但**尽量别这么用**：
 *   裁决悬着期间哨兵已经被这次返回吃掉了，历史里没有东西可拦，
 *   用户再滑一下就直接落到上一页 —— 确认框还开着，人已经被弹走了。
 *   要做「先确认再走」就返回 `'stay'`（自动补哨兵），
 *   等用户点确认时再调 `leaveTopGuard()`。
 */
export function pushBackGuard(onBack: () => BackDecision | Promise<BackDecision>): BackGuard {
  if (!bound) {
    bound = true;
    window.addEventListener('popstate', onPopState);
  }

  const layer: Layer = { onBack, done: false, sentinelAlive: false };
  layers.push(layer);
  pushSentinel(layer);

  return {
    release(dropSentinel = false): void {
      if (layer.done) return;
      const hadSentinel = layer.sentinelAlive;
      dropLayer(layer);
      if (dropSentinel && hadSentinel) backProgrammatically();
    },
  };
}

/** 仅供测试：清空所有层与内部标志 */
export function __resetBackGuards(): void {
  layers.length = 0;
  swallowNextPop = false;
  if (bound) {
    window.removeEventListener('popstate', onPopState);
    bound = false;
  }
}

/**
 * 主动向栈顶守卫要一次裁决（页头的「返回」键用）。
 *
 * 与手势返回的区别：**历史没有退位**，哨兵还压在当前位置上。所以裁决完之后
 * 要自己把历史挪到位：
 *   - `stay`    → 什么都不做（哨兵原样留着，下次手势返回照样拦）
 *   - `dismiss` → 退一格（只弹掉哨兵，回到本页）
 *   - `leave`   → 退两格（哨兵 + 本页，落到真正的上一页）
 *
 * 为什么不让按钮直接 `history.back()`：那条路要求「哨兵一定在栈顶」，
 * 而守卫的挂载/卸载是异步的，一旦错位，按钮就会退错一格（退到本页哨兵上，
 * 看起来「点了没反应」）。这里改成问守卫本身，与历史位置解耦。
 *
 * @returns 是否有人接管。`false` 表示当前没有守卫（调用方自己 `navigate(-1)`）。
 */
export function triggerTopGuard(): boolean {
  const layer = layers[layers.length - 1];
  if (!layer || layer.done) return false;

  void (async () => {
    let decision: BackDecision;
    try {
      decision = await layer.onBack();
    } catch {
      dropLayer(layer);
      return;
    }
    // 裁决期间可能已经被别处收掉了（连点两下）
    if (layer.done) return;
    if (decision === 'stay') return;

    const hadSentinel = layer.sentinelAlive;
    dropLayer(layer);
    const steps = decision === 'leave' ? (hadSentinel ? -2 : -1) : -1;
    if (hadSentinel || decision === 'leave') {
      swallowNextPop = true;
      window.history.go(steps);
    }
  })();

  return true;
}
